<div class="streaming-chat-panel" [class.open]="isOpen">
  <!-- Header slot (custom header content) -->
  <div class="panel-header" *ngIf="headerSlotTemplate">
    <ng-container *ngTemplateOutlet="headerSlotTemplate"></ng-container>
  </div>

  <!-- Header actions slot (e.g., diff notification bar) -->
  <div class="panel-header-actions" *ngIf="headerActionsSlotTemplate">
    <ng-container *ngTemplateOutlet="headerActionsSlotTemplate"></ng-container>
  </div>

  <div class="chat-main" [class.chat-main--empty]="showEmptyState">
  <!-- Empty state (when no messages) -->
  <div class="empty-state" *ngIf="showEmptyState && effectiveEmptyState">
    <ion-icon
      *ngIf="!effectiveEmptyState.icon"
      src="assets/custom-ion-icons/custom-tailor.svg"
    ></ion-icon>
    <ion-icon
      *ngIf="effectiveEmptyState.icon"
      [name]="effectiveEmptyState.icon"
    ></ion-icon>
    <h3>
      {{ effectiveEmptyState.title }}
      <span class="badge" *ngIf="effectiveEmptyState.badge">{{
        effectiveEmptyState.badge
      }}</span>
    </h3>
    <p *ngIf="effectiveEmptyState.description">
      {{ effectiveEmptyState.description }}
    </p>
    <p *ngIf="effectiveEmptyState.keyboardShortcut" class="margin-top-10">
      Type <kbd>{{ effectiveEmptyState.keyboardShortcut }}</kbd> to open.
    </p>
  </div>

  <!-- Messages area -->
  <div class="messages-area" #messagesContainer *ngIf="showMessagesArea">
    <ng-container
      *ngFor="let msg of allMessages; let i = index; trackBy: trackMessage"
    >
      <!-- User message -->
      <div class="message user-message" *ngIf="msg.role === 'user'">
        <div class="message-images" *ngIf="msg.images?.length">
          <img
            *ngFor="let img of msg.images"
            [src]="img"
            alt="Attached image"
            class="message-image"
          />
        </div>
        <div class="message-content" *ngIf="msg.content">{{ msg.content }}</div>
        <!-- User message actions (e.g., resend/delete after cancel) -->
        <div
          class="message-actions user-message-actions"
          *ngIf="msg.actions?.length"
        >
          <button
            *ngFor="let action of msg.actions; trackBy: trackAction"
            class="message-action-btn btn-xs"
            [class.primary]="action.color === 'primary'"
            [class.danger]="action.color === 'danger'"
            [class.outline]="action.fill === 'outline'"
            (click)="onMessageActionClick(action, i)"
          >
            <ion-icon *ngIf="action.icon" [name]="action.icon"></ion-icon>
            {{ action.label }}
          </button>
        </div>
      </div>

      <!-- Assistant message -->
      <div
        class="message assistant-message"
        *ngIf="msg.role === 'assistant'"
        [class.streaming]="msg.metadata?.['isStreaming']"
        [class.error-message]="msg.metadata?.['isError']"
      >
        <!-- Thinking section (collapsible) -->
        <div
          class="thinking-section"
          *ngIf="
            msg.metadata?.['thinkingContent'] ||
            msg.metadata?.['isThinkingActive']
          "
        >
          <div
            class="thinking-header"
            (click)="toggleThinkingExpanded(msg.metadata?.['messageIndex'])"
          >
            <ion-icon
              [name]="
                isThinkingExpanded(msg.metadata?.['messageIndex'])
                  ? 'chevron-down'
                  : 'chevron-forward'
              "
            ></ion-icon>
            <span class="thinking-label">
              <ng-container *ngIf="msg.metadata?.['isThinkingActive']">
                <ion-spinner name="dots"></ion-spinner>
                Thinking...
              </ng-container>
              <ng-container *ngIf="!msg.metadata?.['isThinkingActive']">
                Thought for
                {{ formatDuration(msg.metadata?.["thinkingDurationMs"] || 0) }}
              </ng-container>
            </span>
          </div>
          <div
            class="thinking-content"
            *ngIf="isThinkingExpanded(msg.metadata?.['messageIndex'])"
          >
            {{ msg.metadata?.["thinkingContent"] }}
          </div>
        </div>

        <!-- Processing indicator (shown after thinking completes but before content arrives) -->
        <div
          class="processing-indicator"
          *ngIf="msg.metadata?.['isProcessing']"
        >
          <ion-spinner name="dots"></ion-spinner>
          <span>Processing...</span>
        </div>

        <!-- Content parts (text and tool calls rendered inline) -->
        <div
          class="message-content"
          *ngIf="msg.metadata?.['contentParts']?.length > 0"
        >
          <ng-container
            *ngFor="
              let part of msg.metadata?.['contentParts'];
              let isLast = last
            "
          >
            <!-- Text part (skip empty parts used as separators) -->
            <div
              class="message-text"
              *ngIf="part.type === 'text' && part.text?.trim()"
              [innerHTML]="part.text | markdown"
            ></div>

            <!-- Tool call part (inline, expandable) -->
            <div
              class="tool-call-inline"
              *ngIf="
                part.type === 'tool-call' &&
                shouldShowToolCall(part.toolCall.toolName)
              "
              [class.pending]="part.toolCall.status === 'pending'"
              [class.complete]="part.toolCall.status === 'complete'"
              [class.error]="part.toolCall.status === 'error'"
              [class.expanded]="isToolCallExpanded(part.toolCall.id)"
            >
              <div
                class="tool-call-header"
                (click)="toggleToolCallExpanded(part.toolCall.id)"
              >
                <ion-spinner
                  name="dots"
                  *ngIf="part.toolCall.status === 'pending'"
                ></ion-spinner>
                <ion-icon
                  name="checkmark-circle"
                  *ngIf="part.toolCall.status === 'complete'"
                  color="success"
                ></ion-icon>
                <ion-icon
                  name="close-circle"
                  *ngIf="part.toolCall.status === 'error'"
                  color="danger"
                ></ion-icon>
                <span class="tool-call-name">{{
                  getToolCallLabel(part.toolCall.toolName, part.toolCall.input)
                }}</span>
                <ion-icon
                  class="expand-icon"
                  [name]="
                    isToolCallExpanded(part.toolCall.id)
                      ? 'chevron-up'
                      : 'chevron-down'
                  "
                  *ngIf="hasToolCallDetails(part.toolCall)"
                ></ion-icon>
              </div>
              <div
                class="tool-call-details"
                *ngIf="
                  isToolCallExpanded(part.toolCall.id) &&
                  hasToolCallDetails(part.toolCall)
                "
              >
                <pre>{{ getToolCallDetails(part.toolCall) }}</pre>
              </div>
            </div>

            <!-- Sub-agent progress part -->
            <div
              class="sub-agent-inline"
              *ngIf="part.type === 'sub-agent'"
              [class.running]="part.subAgent.status === 'running'"
              [class.done]="part.subAgent.status === 'done'"
              [class.error]="part.subAgent.status === 'error'"
            >
              <div
                class="sub-agent-header"
                (click)="toggleToolCallExpanded(part.subAgent.name)"
              >
                <ion-spinner
                  name="dots"
                  *ngIf="part.subAgent.status === 'running'"
                ></ion-spinner>
                <ion-icon
                  name="checkmark-circle"
                  *ngIf="part.subAgent.status === 'done'"
                  color="success"
                ></ion-icon>
                <ion-icon
                  name="close-circle"
                  *ngIf="part.subAgent.status === 'error'"
                  color="danger"
                ></ion-icon>
                <span class="sub-agent-label">{{ part.subAgent.label }}</span>
                <ion-icon
                  class="expand-icon"
                  [name]="
                    isToolCallExpanded(part.subAgent.name)
                      ? 'chevron-up'
                      : 'chevron-down'
                  "
                  *ngIf="part.subAgent.toolCalls?.length > 0 || part.subAgent.textContent"
                ></ion-icon>
              </div>
              <div
                class="sub-agent-details"
                *ngIf="isToolCallExpanded(part.subAgent.name)"
              >
                <!-- Sub-agent's streamed assistant text. -->
                <div
                  class="sub-agent-text"
                  *ngIf="part.subAgent.textContent"
                  [innerHTML]="part.subAgent.textContent | markdown"
                ></div>
                <div
                  class="tool-call-inline nested"
                  *ngFor="let tc of part.subAgent.toolCalls"
                  [class.pending]="tc.status === 'pending'"
                  [class.complete]="tc.status === 'complete'"
                >
                  <div class="tool-call-header">
                    <ion-spinner
                      name="dots"
                      *ngIf="tc.status === 'pending'"
                    ></ion-spinner>
                    <ion-icon
                      name="checkmark-circle"
                      *ngIf="tc.status === 'complete'"
                      color="success"
                    ></ion-icon>
                    <span class="tool-call-name">{{
                      getToolCallLabel(tc.toolName, tc.input)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- HITL approval card -->
            <div
              class="hitl-approval-card"
              *ngIf="part.type === 'hitl-approval'"
              [class.resolved]="part.approval.resolved"
              [class.approved]="part.approval.resolution === 'approved'"
              [class.rejected]="part.approval.resolution === 'rejected'"
            >
              <div class="hitl-approval-header">
                <ion-icon name="shield-checkmark-outline" *ngIf="part.approval.type === 'tool_approval'"></ion-icon>
                <ion-icon name="key-outline" *ngIf="part.approval.type === 'oauth' || part.approval.type === 'api_key'"></ion-icon>
                <span class="hitl-approval-title" *ngIf="part.approval.type === 'tool_approval'">
                  {{ (part.approval.tools && part.approval.tools.length > 1) ? 'Plan requires approval' : 'Action requires approval' }}
                </span>
                <span class="hitl-approval-title" *ngIf="part.approval.type === 'oauth'">
                  Authentication required
                </span>
                <span class="hitl-approval-title" *ngIf="part.approval.type === 'api_key'">
                  API key required
                </span>
              </div>
              <div class="hitl-approval-body">
                <!--
                  Tool approval: render every queued destructive call.
                  When the batch has multiple entries (a "plan"), show the tool name plus a
                  per-entry parameter expander only — repeating the static tool description
                  for each row adds noise. The agent's prose above the card already
                  contextualizes which conversation each call targets.
                -->
                <ng-container *ngIf="part.approval.type === 'tool_approval' && part.approval.tools">
                  <div class="hitl-tool-entry" *ngFor="let tool of part.approval.tools; let i = index">
                    <div class="hitl-tool-name">{{ tool.toolName }}</div>
                    <div
                      class="hitl-tool-description"
                      *ngIf="tool.toolDescription && part.approval.tools.length === 1"
                    >
                      {{ tool.toolDescription }}
                    </div>
                    <div
                      class="hitl-tool-args"
                      *ngIf="tool.toolArgs && !part.approval.resolved"
                      (click)="toggleToolCallExpanded('hitl-' + part.approval.runId + '-' + i)"
                    >
                      <span class="hitl-args-toggle">
                        <ion-icon [name]="isToolCallExpanded('hitl-' + part.approval.runId + '-' + i) ? 'chevron-up' : 'chevron-down'"></ion-icon>
                        View parameters
                      </span>
                      <pre *ngIf="isToolCallExpanded('hitl-' + part.approval.runId + '-' + i)">{{ tool.toolArgs | json }}</pre>
                    </div>
                  </div>
                </ng-container>
                <!-- OAuth / API key: legacy single-tool fields -->
                <ng-container *ngIf="part.approval.type !== 'tool_approval'">
                  <div class="hitl-tool-name">{{ part.approval.toolName }}</div>
                  <div class="hitl-tool-description" *ngIf="part.approval.toolDescription">
                    {{ part.approval.toolDescription }}
                  </div>
                  <div class="hitl-oauth-provider" *ngIf="part.approval.oauthConnectionName && part.approval.type !== 'api_key'">
                    Connect to {{ part.approval.oauthConnectionName }}
                  </div>
                  <div class="hitl-api-key-provider" *ngIf="part.approval.oauthConnectionName && part.approval.type === 'api_key'">
                    Enter your API key for {{ part.approval.oauthConnectionName }}
                  </div>
                </ng-container>
              </div>
              <!-- API key input -->
              <div class="hitl-api-key-input" *ngIf="part.approval.type === 'api_key' && !part.approval.resolved">
                <input
                  [disabled]="isLoading || part.approval.invalidated || part.approval.answering"
                  type="password"
                  class="hitl-api-key-field"
                  placeholder="Paste your API key"
                  #apiKeyInput
                  (keydown.enter)="!isLoading && !part.approval.invalidated && !part.approval.answering && hitlApprovalClick.emit({ approval: part.approval, approved: true, apiKey: apiKeyInput.value })"
                />
                <div class="hitl-approval-actions">
                  <button
                    [disabled]="isLoading || part.approval.invalidated || part.approval.answering"
                    class="hitl-btn hitl-btn-reject"
                    (click)="hitlApprovalClick.emit({ approval: part.approval, approved: false })"
                  >
                    <ion-icon name="close-outline"></ion-icon>
                    Cancel
                  </button>
                  <button
                    [disabled]="isLoading || part.approval.invalidated || part.approval.answering"
                    class="hitl-btn hitl-btn-approve"
                    (click)="hitlApprovalClick.emit({ approval: part.approval, approved: true, apiKey: apiKeyInput.value })"
                  >
                    <ion-icon name="checkmark-outline"></ion-icon>
                    Submit
                  </button>
                </div>
              </div>
              <!-- OAuth / Tool approval actions -->
              <div class="hitl-approval-actions" *ngIf="part.approval.type !== 'api_key' && !part.approval.resolved">
                <button
                  [disabled]="isLoading || part.approval.invalidated || part.approval.answering"
                  class="hitl-btn hitl-btn-reject"
                  (click)="hitlApprovalClick.emit({ approval: part.approval, approved: false })"
                >
                  <ion-icon name="close-outline"></ion-icon>
                  {{ (part.approval.tools && part.approval.tools.length > 1) ? 'Reject all' : 'Reject' }}
                </button>
                <button
                  [disabled]="isLoading || part.approval.invalidated || part.approval.answering"
                  class="hitl-btn hitl-btn-approve"
                  (click)="hitlApprovalClick.emit({ approval: part.approval, approved: true })"
                >
                  <ion-icon name="checkmark-outline"></ion-icon>
                  {{ (part.approval.tools && part.approval.tools.length > 1) ? 'Approve all' : 'Approve' }}
                </button>
              </div>
              @if (part.approval.invalidated) {
                <div class="hitl-approval-resolved">No longer available</div>
              }
              <div class="hitl-approval-resolved" *ngIf="part.approval.resolved">
                <ion-icon name="checkmark-circle" color="success" *ngIf="part.approval.resolution === 'approved'"></ion-icon>
                <ion-icon name="close-circle" color="danger" *ngIf="part.approval.resolution === 'rejected'"></ion-icon>
                <span>{{ part.approval.resolution === 'approved' ? 'Approved' : 'Rejected' }}</span>
              </div>
            </div>

            <!-- Streaming indicator at the end of content -->
            <span
              class="streaming-indicator"
              *ngIf="
                isLast &&
                msg.metadata?.['isStreaming'] &&
                part.type === 'text' &&
                part.text?.trim()
              "
            >
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </span>
          </ng-container>
        </div>

        <!-- Fallback: Message content without parts (for old messages or errors) -->
        <div
          class="message-content"
          *ngIf="
            msg.content &&
            (!msg.metadata?.['contentParts'] ||
              msg.metadata?.['contentParts']?.length === 0)
          "
        >
          <div class="message-text" [innerHTML]="msg.content | markdown"></div>
          <span
            class="streaming-indicator"
            *ngIf="msg.metadata?.['isStreaming']"
          >
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </span>
        </div>

        <!-- Message actions (e.g., retry, custom actions) -->
        <div class="message-actions" *ngIf="msg.actions?.length">
          <button
            *ngFor="let action of msg.actions; trackBy: trackAction"
            class="message-action-btn btn-xs"
            [class.primary]="action.color === 'primary'"
            [class.danger]="action.color === 'danger'"
            [class.outline]="action.fill === 'outline'"
            (click)="onMessageActionClick(action, i)"
          >
            <ion-icon *ngIf="action.icon" [name]="action.icon"></ion-icon>
            {{ action.label }}
          </button>
        </div>
      </div>
    </ng-container>
  </div>

  <!-- Footer slot (above input) -->
  <div class="panel-footer" *ngIf="footerSlotTemplate">
    <ng-container *ngTemplateOutlet="footerSlotTemplate"></ng-container>
  </div>

  <!-- Input area -->
  <div
    class="input-area"
    [class.drag-over]="isDragging"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
  >
    <div class="input-actions" *ngIf="messages.length > 0 && !config.hideInputActions">
      <ion-button fill="clear" size="small" (click)="onNewChatClick()">
        <ion-icon name="create-outline" slot="start"></ion-icon>
        New chat
      </ion-button>
      <ion-button
        fill="clear"
        size="small"
        color="danger"
        *ngIf="isLoading"
        (click)="onCancelClick()"
      >
        <ion-icon name="close" slot="start"></ion-icon>
        Cancel
      </ion-button>
    </div>

    <!-- Queued message indicator -->
    <div class="queued-indicator" *ngIf="queuedMessage">
      <ion-icon name="time-outline"></ion-icon>
      <span
        >Message queued: "{{ queuedMessage | slice: 0 : 50
        }}{{ queuedMessage.length > 50 ? "..." : "" }}"</span
      >
      <ion-button
        fill="clear"
        size="small"
        color="danger"
        (click)="onClearQueuedMessage()"
      >
        <ion-icon name="close-circle"></ion-icon>
      </ion-button>
    </div>

    <!-- Image previews -->
    <div class="image-previews" *ngIf="pendingImages.length > 0">
      <div
        class="image-preview"
        *ngFor="let img of pendingImages; let i = index"
      >
        <img [src]="img" alt="Attached image" />
        <button class="remove-image-btn" (click)="removeImage(i)">
          <ion-icon name="close-circle"></ion-icon>
        </button>
      </div>
    </div>

    <div class="message-composer" [class.loading]="isLoading">
      <ion-card class="message-composer-inner">
        <ion-textarea
          #inputTextarea
          [attr.aria-label]="placeholder"
          autoGrow
          [rows]="3"
          [placeholder]="placeholder"
          [(ngModel)]="inputValue"
          (keydown)="onInputKeyDown($event)"
        ></ion-textarea>
        <div class="composer-actions">
          <ion-button
            class="attach-btn btn-small"
            fill="clear"
            color="dark"
            (click)="onAttachClick()"
          >
            <ion-icon name="image-outline" style="font-size: 1.2rem"></ion-icon>
          </ion-button>
          <ion-button
            class="send-btn btn-small"
            fill="clear"
            [disabled]="!inputValue?.trim() && !pendingImages.length"
            (click)="onSendClick()"
            [color]="
              !inputValue?.trim() && !pendingImages.length ? 'medium' : 'primary'
            "
          >
            <ion-icon
              [name]="isLoading ? 'hourglass-outline' : 'arrow-up-circle'"
              style="font-size: 1.5rem"
            ></ion-icon>
          </ion-button>
        </div>
      </ion-card>

      <!-- Hidden file input for image selection -->
      <input
        #fileInput
        type="file"
        accept="image/*"
        multiple
        style="display: none"
        (change)="onFileSelected($event)"
      />
      <div
        class="input-footer"
        [class.input-footer--visible]="showInputFooterHints"
        [attr.aria-hidden]="showInputFooterHints ? null : 'true'"
      >
        <span class="keyboard-hint"
          ><kbd>Enter</kbd> send · <kbd>Shift+Enter</kbd> new line</span
        >
        <ng-container *ngIf="modelHint">
          <span class="separator">·</span>
          <span class="model-hint">{{ modelHint }}</span>
        </ng-container>
      </div>
    </div>

    <div
      class="quick-actions quick-actions--below-input"
      *ngIf="showEmptyState && effectiveEmptyState?.presets?.length"
    >
      <button
        class="quick-action-btn"
        *ngFor="let preset of effectiveEmptyState.presets"
        (click)="onPresetClick(preset)"
      >
        <ion-icon *ngIf="preset.icon" [name]="preset.icon"></ion-icon>
        {{ preset.label }}
      </button>
    </div>
  </div>
  </div>

  <!-- Custom actions slot (for domain-specific actions like diff notification) -->
  <ng-container *ngTemplateOutlet="customActionsTemplate"></ng-container>
</div>
